import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { GCBadgeSVG, SCBadgeSVG } from '@/components/CurrencyBadge';
import { cn } from '@/lib/utils';
import { X, Sparkles, Gift, Crown, Star, ChevronRight } from 'lucide-react';

interface ScheduledOffer {
  id: number;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number | null;
  cadence_type: string;
  resets_at: string | null;
  can_purchase: boolean;
  purchases_remaining: number;
  group_id: number | null;
  is_welcome_offer: boolean;
}

interface ScheduledOfferGroup {
  id: number;
  title: string;
  offers: ScheduledOffer[];
  is_welcome_group: boolean;
}

interface WelcomeOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  welcomeOffer: ScheduledOffer | null;  // Single ungrouped offer
  welcomeGroup: ScheduledOfferGroup | null;  // Single group
  hasMoreOffers: boolean;  // Are there more offers after this one?
  onPurchase: (offer: ScheduledOffer) => void;  // Pass full offer for payment
}

// Format coins helper
const formatCoins = (amount: number): string => {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(0)}K`;
  }
  return amount.toString();
};

// Format price helper
const formatPrice = (price: number): string => {
  return `$${price.toFixed(2)}`;
};

export const WelcomeOfferModal = ({
  isOpen,
  onClose,
  welcomeOffer,
  welcomeGroup,
  hasMoreOffers,
  onPurchase,
}: WelcomeOfferModalProps) => {
  // Determine what we're showing
  const isGroupOffer = welcomeGroup !== null;
  
  // Get sorted offers from group (if showing a group)
  const groupOffers = useMemo(() => {
    if (!welcomeGroup) return [];
    return [...welcomeGroup.offers]
      .filter(o => o.can_purchase !== false && (o.purchases_remaining ?? 1) > 0)
      .sort((a, b) => a.price - b.price)
      .slice(0, 6);
  }, [welcomeGroup]);
  
  // State for group offer selection
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // Get the currently selected offer
  const selectedOffer = isGroupOffer ? groupOffers[selectedIndex] : welcomeOffer;
  
  // Reset selection when modal opens or offer changes
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
    }
  }, [isOpen, welcomeGroup, welcomeOffer]);
  
  if (!selectedOffer) return null;
  
  const totalGC = selectedOffer.gc_amount + selectedOffer.gc_bonus_amount;
  const totalSC = selectedOffer.sc_amount + selectedOffer.sc_bonus_amount;
  const hasDiscount = selectedOffer.original_price && selectedOffer.original_price > selectedOffer.price;
  const discountPercent = hasDiscount
    ? Math.round((1 - selectedOffer.price / selectedOffer.original_price!) * 100)
    : 0;
  
  // Calculate bonus percentage
  const gcBonusPercent = selectedOffer.gc_amount > 0 
    ? Math.round((selectedOffer.gc_bonus_amount / selectedOffer.gc_amount) * 100) 
    : 0;
  const scBonusPercent = selectedOffer.sc_amount > 0 
    ? Math.round((selectedOffer.sc_bonus_amount / selectedOffer.sc_amount) * 100) 
    : 0;
  const bonusPercent = Math.max(gcBonusPercent, scBonusPercent);

  const handlePurchase = () => {
    onPurchase(selectedOffer);
    // Note: onClose() is handled by the parent after payment initiation
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-lg w-[95vw] p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button]:hidden"
        aria-describedby={undefined}
      >
        <div className="relative bg-gradient-to-b from-amber-900 via-yellow-900 to-amber-950 rounded-2xl overflow-hidden border-2 border-yellow-400/60 shadow-[0_0_60px_rgba(251,191,36,0.4)]">
          {/* Animated background effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Radial glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200%] h-[60%] bg-gradient-radial from-yellow-400/30 via-amber-500/10 to-transparent" />
            
            {/* Sparkle decorations */}
            <Sparkles className="absolute top-8 left-8 h-6 w-6 text-yellow-300 animate-pulse" />
            <Sparkles className="absolute top-12 right-10 h-5 w-5 text-amber-300 animate-pulse" style={{ animationDelay: '0.3s' }} />
            <Sparkles className="absolute bottom-32 left-6 h-4 w-4 text-yellow-400 animate-pulse" style={{ animationDelay: '0.6s' }} />
            <Sparkles className="absolute bottom-24 right-8 h-5 w-5 text-amber-200 animate-pulse" style={{ animationDelay: '0.9s' }} />
            <Star className="absolute top-20 left-1/4 h-3 w-3 text-yellow-300/60 animate-twinkle" />
            <Star className="absolute top-16 right-1/4 h-4 w-4 text-amber-300/60 animate-twinkle" style={{ animationDelay: '0.5s' }} />
            
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-200/10 to-transparent animate-shimmer" />
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-colors border border-yellow-500/30"
          >
            <X className="h-5 w-5 text-white" />
          </button>

          {/* Content */}
          <div className="relative z-10 p-6 sm:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 mb-4 shadow-lg shadow-yellow-500/50">
                <Gift className="h-8 w-8 text-amber-900" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-amber-100 to-yellow-200 mb-2">
                WELCOME BONUS
              </h2>
              <p className="text-amber-200/80 text-sm sm:text-base">
                Exclusive offer just for you!
              </p>
            </div>

            {/* Bonus badge */}
            {bonusPercent > 0 && (
              <div className="flex items-center justify-center mb-6">
                <div className="px-4 py-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-400 text-white font-black text-sm shadow-lg animate-pulse">
                  +{bonusPercent}% BONUS
                </div>
              </div>
            )}

            {/* Group offers - Option selector */}
            {isGroupOffer && groupOffers.length > 1 && (
              <div className="mb-6">
                <p className="text-center text-amber-300/80 text-sm mb-3 font-medium">
                  Choose your welcome package:
                </p>
                <div className="grid grid-cols-1 gap-2 max-h-[280px] overflow-y-auto pr-1">
                  {groupOffers.map((offer, index) => {
                    const offerGC = offer.gc_amount + offer.gc_bonus_amount;
                    const offerSC = offer.sc_amount + offer.sc_bonus_amount;
                    const isSelected = index === selectedIndex;
                    const canBuy = offer.can_purchase !== false && (offer.purchases_remaining ?? 1) > 0;

                    return (
                      <button
                        key={offer.id}
                        onClick={() => setSelectedIndex(index)}
                        disabled={!canBuy}
                        className={cn(
                          "w-full px-4 py-3 rounded-xl transition-all flex items-center justify-between",
                          isSelected
                            ? "bg-gradient-to-r from-yellow-400 to-amber-400 text-amber-900 ring-2 ring-yellow-300 shadow-lg shadow-yellow-500/40 scale-[1.02]"
                            : canBuy
                              ? "bg-amber-900/60 text-amber-100 hover:bg-amber-800/70 border border-amber-500/40"
                              : "bg-muted text-muted-foreground cursor-not-allowed"
                        )}
                      >
                        {/* Left side: GC + SC */}
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5">
                            <GCBadgeSVG size={20} />
                            <span className={cn("font-black text-base", isSelected ? "text-amber-900" : "text-amber-200")}>
                              {formatCoins(offerGC)}
                            </span>
                          </div>
                          {offerSC > 0 && (
                            <>
                              <span className={cn("text-sm", isSelected ? "text-amber-800/70" : "text-amber-400/70")}>+</span>
                              <div className="flex items-center gap-1.5">
                                <SCBadgeSVG size={20} />
                                <span className={cn("font-bold text-sm", isSelected ? "text-green-700" : "text-green-400")}>
                                  {formatCoins(offerSC)}
                                </span>
                                <span className={cn("text-xs font-semibold", isSelected ? "text-green-700" : "text-green-400/80")}>
                                  FREE
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Right side: Price */}
                        <div className={cn(
                          "font-black text-base px-3 py-1 rounded-lg",
                          isSelected 
                            ? "bg-amber-900/30 text-amber-900" 
                            : "bg-amber-950/50 text-amber-100"
                        )}>
                          {formatPrice(offer.price)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Single offer display (ungrouped offer or group with only 1 offer) */}
            {(!isGroupOffer || groupOffers.length === 1) && (
              <div className="bg-gradient-to-br from-amber-800/50 to-amber-900/50 rounded-2xl p-6 mb-6 border border-amber-500/30">
                {/* Currency display */}
                <div className="flex flex-col items-center gap-4">
                  {/* GC */}
                  <div className="flex items-center gap-3">
                    <GCBadgeSVG size={32} />
                    <span className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-200">
                      {formatCoins(totalGC)}
                    </span>
                  </div>
                  
                  {/* SC */}
                  {totalSC > 0 && (
                    <div className="flex items-center gap-3">
                      <span className="text-amber-400/60 text-2xl">+</span>
                      <SCBadgeSVG size={32} />
                      <span className="text-4xl font-black text-green-400">
                        {formatCoins(totalSC)}
                      </span>
                      <span className="text-green-400/70 text-xl font-bold">FREE</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Purchase button */}
            <Button
              onClick={handlePurchase}
              disabled={!selectedOffer.can_purchase}
              className={cn(
                "w-full h-14 sm:h-16 rounded-xl text-lg sm:text-xl font-black relative overflow-hidden group",
                "bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-400",
                "hover:from-yellow-300 hover:via-amber-300 hover:to-yellow-300",
                "text-amber-900 shadow-lg shadow-yellow-500/40",
                "border-2 border-yellow-300/50",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                {hasDiscount && selectedOffer.original_price && (
                  <span className="line-through text-amber-700/60 text-base">
                    {formatPrice(selectedOffer.original_price)}
                  </span>
                )}
                <span className="text-2xl">{formatPrice(selectedOffer.price)}</span>
                <ChevronRight className="h-6 w-6" />
              </span>
              
              {/* Button shimmer */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
            </Button>

            {/* Limited time notice or more offers hint */}
            <p className="text-center text-amber-300/60 text-xs mt-4">
              {hasMoreOffers 
                ? "More welcome bonuses available after this one!" 
                : "Limited time offer for new players"
              }
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
